import React from "react";

import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";

import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Typography,
} from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

import { useTranslation } from "react-i18next";

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
};

const EmployeeGrid = ({
  employees = [],

  loading = false,

  onEdit = () => {},

  onDelete = () => {},
}) => {
  const { t, i18n } = useTranslation("UserManagement");
  const columns = [
    // ==========================
    // NAME
    // ==========================

    {
      field: "fullName",

      headerName: t("Employee"),

      flex: 1,

      minWidth: 220,

      renderCell: (params) => {
        const initials = `${params.row.firstName?.[0] || ""}
          ${params.row.lastName?.[0] || ""}`;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Avatar>{initials}</Avatar>

            <Box>
              {params.row.firstName} {params.row.lastName}
            </Box>
          </Box>
        );
      },
    },

    // ==========================
    // USERNAME
    // ==========================

    {
      field: "username",

      headerName: t("Username"),

      flex: 1,

      minWidth: 150,
    },

    // ==========================
    // EMAIL
    // ==========================

    {
      field: "email",

      headerName: t("Email"),

      flex: 1.2,

      minWidth: 220,
    },

    // ==========================
    // DEPARTMENT
    // ==========================

    {
      field: "department",
      headerName: t("Department"),
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: "100%",
          }}
        >
          <Typography variant="body2">{params.value}</Typography>
          {params.row.estOperateur && (
            <StarRoundedIcon
              sx={{
                color: "#FFC107",
                fontSize: 18,
              }}
            />
          )}
        </Box>
      ),
    },
    // ==========================
    // STATUS
    // ==========================

    {
      field: "isInactive",

      headerName: t("Status"),

      width: 130,

      renderCell: (params) => (
        <Chip
          label={params.value ? t("Inactive") : t("Active")}
          color={params.value ? "error" : "success"}
          size="small"
        />
      ),
    },

    // ==========================
    // OPERATOR
    // ==========================

    {
      field: "estOperateur",

      headerName: t("Operator"),

      width: 130,

      renderCell: (params) => (
        <Chip
          label={params.value ? t("Yes") : t("No")}
          color={params.value ? "primary" : "default"}
          size="small"
        />
      ),
    },

    // ==========================
    // LAST LOGIN
    // ==========================

    {
      field: "lastLogin",

      headerName: t("LastLogin"),

      width: 180,

      renderCell: (params) => {
        if (!params.value) return "-";

        return new Date(params.value).toLocaleString(
          i18n.language === "FR" ? "fr-CA" : "en-CA",
        );
      },
    },

    // ==========================
    // ACTIONS
    // ==========================

    {
      field: "actions",

      headerName: t("Actions"),

      width: 130,

      sortable: false,

      filterable: false,

      renderCell: (params) => (
        <Box>
          <Tooltip title={t("Edit")}>
            <IconButton color="primary" onClick={() => onEdit(params.row)}>
              <EditRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("Delete")}>
            <IconButton color="error" onClick={() => onDelete(params.row)}>
              <DeleteRoundedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",

        height: 650,
      }}
    >
      <DataGridPro
        rows={employees}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.id}
        pagination
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        slots={{
          toolbar: () => <CustomToolbar t={t} />,
        }}
        sx={{
          borderRadius: 3,

          "& .MuiDataGrid-columnHeaders": {
            fontWeight: 700,
          },
        }}
      />
    </Box>
  );
};

export default EmployeeGrid;
